import { stepDetailInputVisible } from './medicalQuestionnaireChoiceOptions';

export function detailTriggerValuesNormalized(step) {
  if (Array.isArray(step.detailTriggerValues) && step.detailTriggerValues.length) {
    return step.detailTriggerValues.map((t) => String(t || '').trim().toLowerCase());
  }
  return ['yes'];
}

function isMultiSelectStep(step) {
  return Boolean(
    step?.multi || (step?.validation?.type === 'array' && step?.type !== 'yesNoWithTextarea')
  );
}

function isNumericValidationStep(step) {
  if (isMultiSelectStep(step)) return false;
  if (step?.type === 'choiceList') return false;

  const v = step?.validation;
  if (!v || typeof v !== 'object') return step?.type === 'number';
  const validationType = String(v.type || '').toLowerCase();
  return step?.type === 'number' || validationType === 'number';
}

/** Block Continue when selection count exceeds validation.max from API rules. */
export function getStepMaxSelectionMessage(step, value) {
  const max = step?.maxSelections ?? step?.validation?.max;
  if (!Number.isFinite(max) || max <= 0) return null;
  const count = Array.isArray(value) ? value.length : value != null && String(value).trim() ? 1 : 0;
  if (count <= max) return null;
  if (max === 1) return 'Selecteer maximaal één optie.';
  return `Selecteer maximaal ${max} opties.`;
}

/** Block Continue when selection count is below validation.min from API rules. */
export function getStepMinSelectionMessage(step, value) {
  if (!isMultiSelectStep(step)) return null;
  const min = Number(step?.validation?.min);
  if (!Number.isFinite(min) || min <= 0) return null;
  const count = Array.isArray(value) ? value.length : 0;
  if (count >= min) return null;
  if (min === 1) return 'Selecteer minstens één optie.';
  return `Selecteer minstens ${min} opties.`;
}

/** Enforce validation.min / validation.max for numeric questions from API rules. */
export function getStepNumericRangeMessage(step, value) {
  if (!isNumericValidationStep(step)) return null;
  if (Array.isArray(value)) return null;
  const v = step?.validation;
  const raw = String(value ?? '').trim();
  if (!raw) return null;

  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    return 'Voer een geldig getal in.';
  }

  const min = Number(v?.min);
  const max = Number(v?.max);
  if (Number.isFinite(min) && parsed < min) {
    return `Voer een waarde van minimaal ${min} in.`;
  }
  if (Number.isFinite(max) && parsed > max) {
    return `Voer een waarde van maximaal ${max} in.`;
  }
  if (v?.integer === true && !Number.isInteger(parsed)) {
    return 'Voer een heel getal in.';
  }
  return null;
}

function isStepValuePresent(step, value, detailValue) {
  if (step?.type === 'yesNoWithTextarea') {
    const answer = String(value || '').trim();
    if (!answer) return false;
    if (stepDetailInputVisible(step, answer)) {
      return String(detailValue ?? '').trim().length > 0;
    }
    return true;
  }
  if (isMultiSelectStep(step)) {
    return Array.isArray(value) && value.length > 0;
  }
  if (step?.type === 'choiceList') {
    return String(value ?? '').trim().length > 0;
  }
  return String(value ?? '').trim().length > 0;
}

/** True when an optional step has no answer (user left it blank). */
export function isOptionalStepWithoutInput(step, value, detailValue) {
  if (!step?.optional) return false;
  return !isStepValuePresent(step, value, detailValue);
}

/** Whether the current answer satisfies API validation rules (used for progress + Continue). */
export function isStepValueValid(step, value) {
  if (step?.optional) {
    return true;
  }
  const detail = step?._detailValue ?? '';
  if (!isStepValuePresent(step, value, detail)) {
    return false;
  }
  if (getStepMaxSelectionMessage(step, value)) return false;
  if (getStepMinSelectionMessage(step, value)) return false;
  if (getStepNumericRangeMessage(step, value)) return false;
  return true;
}

/** Message when user tries to continue without a valid answer. */
export function getStepRequiredEmptyMessage(step, value, detailValue) {
  if (step?.optional) return null;

  const stepWithDetail = { ...step, _detailValue: detailValue };

  const maxMsg = getStepMaxSelectionMessage(step, value);
  if (maxMsg) return maxMsg;

  const minSelMsg = getStepMinSelectionMessage(step, value);
  if (minSelMsg) return minSelMsg;

  if (!isStepValuePresent(stepWithDetail, value, detailValue)) {
    if (step?.type === 'yesNoWithTextarea') {
      if (!String(value ?? '').trim()) return 'Selecteer een optie.';
      return 'Dit veld is verplicht.';
    }
    if (isMultiSelectStep(step)) return 'Selecteer minstens één optie.';
    if (step?.type === 'choiceList') return 'Dit veld is verplicht.';
    return 'Dit veld is verplicht.';
  }

  const rangeMsg = getStepNumericRangeMessage(step, value);
  if (rangeMsg) return rangeMsg;

  return null;
}

function isQuestionAnswered(question, answers) {
  const value = answers[question.key] ?? (question.multi ? [] : '');
  const detail = question.detailKey ? answers[question.detailKey] || '' : '';
  if (question.multi) {
    return Array.isArray(value) && value.length > 0;
  }
  if (String(value ?? '').trim()) return true;
  return Boolean(String(detail).trim());
}

function isSectionComplete(section, answers, completedSteps = {}) {
  const required = section.questions.filter((q) => !q.optional);
  if (required.length === 0) {
    return section.questions.some(
      (q) => completedSteps[q.key] || isQuestionAnswered(q, answers)
    );
  }
  return required.every((question) => {
    if (completedSteps[question.key]) return true;
    const value = answers[question.key] ?? (question.multi ? [] : '');
    const detail = question.detailKey ? answers[question.detailKey] || '' : '';
    return isStepValueValid({ ...question, _detailValue: detail }, value);
  });
}

/** Steps already answered (from API prefill or current session). */
export function buildCompletedStepsFromAnswers(steps, answers) {
  const completed = {};
  if (!Array.isArray(steps)) return completed;
  for (const step of steps) {
    if (step.optional) {
      if (isQuestionAnswered(step, answers)) completed[step.key] = true;
      continue;
    }
    const value = answers[step.key] ?? (step.multi ? [] : '');
    const detail = step.detailKey ? answers[step.detailKey] || '' : '';
    if (isStepValueValid({ ...step, _detailValue: detail }, value)) {
      completed[step.key] = true;
    }
  }
  return completed;
}

export function getSectionCompletionMap(sections, answers, completedSteps = {}) {
  if (!Array.isArray(sections)) return {};
  return sections.reduce((acc, section, sectionIdx) => {
    acc[sectionIdx] = isSectionComplete(section, answers, completedSteps);
    return acc;
  }, {});
}

export function countFilledQuestionnaireSections(sections, answers, completedSteps = {}) {
  if (!Array.isArray(sections) || !sections.length) {
    return { filledSections: 0, totalSections: 0 };
  }
  let filledSections = 0;
  for (const section of sections) {
    if (isSectionComplete(section, answers, completedSteps)) filledSections += 1;
  }
  return { filledSections, totalSections: sections.length };
}

/**
 * 1-based question index for resume navigation (first required step without a valid answer).
 * @param {Array<object>} steps - flat steps from {@link flatStepsFromSections}
 * @param {Record<string, unknown>} answers
 */
export function resolveNextQuestionOneBasedFromSteps(steps, answers) {
  if (!Array.isArray(steps) || !steps.length) return 1;
  for (let i = 0; i < steps.length; i++) {
    const step = steps[i];
    if (step.optional) continue;
    const value = answers[step.key] ?? (step.multi ? [] : '');
    const detail = step.detailKey ? answers[step.detailKey] || '' : '';
    if (!isStepValueValid({ ...step, _detailValue: detail }, value)) {
      return i + 1;
    }
  }
  return 1;
}
